import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_BODY_RESULT_OPTIONS,
  getAllowedResultOptions,
  mostSevereStatus,
  readStatusList,
  toggleResultStatus,
} from '../utils/resultOptions';

export default function BodyPartStatusModal({ open, item, value, onClose, onSave, resultOptions = DEFAULT_BODY_RESULT_OPTIONS }) {
  // اختيار متعدد: يمكن أن يكون الجزء «خدش + مرشوش» معًا.
  const [statuses, setStatuses] = useState([]);
  const [notes, setNotes] = useState('');
  const itemId = item?.id ?? null;
  const allowed = useMemo(() => (
    Array.isArray(item?.allowed_result_types) && item.allowed_result_types.length
      ? item.allowed_result_types
      : resultOptions.map((option) => option.value)
  ), [item?.allowed_result_types, resultOptions]);

  const visibleOptions = useMemo(
    () => getAllowedResultOptions(resultOptions, allowed),
    [allowed, resultOptions],
  );

  useEffect(() => {
    if (!open || !itemId) return;
    const existing = readStatusList(value);
    setStatuses(existing.length ? existing : []);
    setNotes(value?.notes || '');
  }, [open, itemId, value, visibleOptions]);

  const toggleStatus = (statusValue) => {
    setStatuses((current) => toggleResultStatus(current, statusValue));
  };

  if (!open || !item) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
      <div className="w-full max-w-xl rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b px-6 py-5">
          <div>
            <h3 className="text-lg font-bold text-slate-900">تحديث حالة جزء البودي</h3>
            <p className="mt-1 text-sm text-slate-500">{item.name}</p>
            {item.diagram_zone_key && <p className="mt-1 text-xs text-slate-400">Zone ID: {item.diagram_zone_key}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 px-3 py-1 text-sm text-slate-500 transition hover:bg-slate-100"
          >
            إغلاق
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">الحالة</label>
            <p className="mb-3 text-xs text-slate-500">يمكن اختيار أكثر من نتيجة للجزء الواحد.</p>
            {/* نتيجتان في كل سطر على الجوال حتى لا تملأ القائمة الشاشة طولًا. */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3">
              {visibleOptions.map((option) => {
                const selected = statuses.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleStatus(option.value)}
                    className={`rounded-2xl border px-3 py-3 sm:px-4 sm:py-4 text-right transition ${
                      selected ? 'ring-2 ring-offset-2 ring-slate-900' : 'opacity-60 hover:opacity-100'
                    }`}
                    style={{
                      backgroundColor: option.bgColor,
                      borderColor: option.borderColor,
                      color: option.textColor,
                    }}
                  >
                    <div className="font-bold">{selected ? '✓ ' : ''}{option.label}</div>
                    {option.description && (
                      <div className="mt-1 text-[11px] leading-snug opacity-80 sm:text-xs">{option.description}</div>
                    )}
                  </button>
                );
              })}
            </div>
            {statuses.length > 1 && (
              <p className="mt-3 text-xs text-slate-500">
                لون الجزء ورقمه في الرسم يتبعان الحالة الأشد من بين المختارة.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="body-part-notes" className="mb-2 block text-sm font-semibold text-slate-700">
              ملاحظات
            </label>
            <textarea
              id="body-part-notes"
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder=""
              className="w-full rounded-2xl border border-slate-200 p-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            إلغاء
          </button>
          <button
            type="button"
            onClick={() => onSave({
              condition_statuses: statuses,
              condition_status: mostSevereStatus(statuses),
              notes,
            })}
            className="rounded-xl bg-primary px-5 py-2 text-sm font-bold text-white transition hover:opacity-90"
          >
            حفظ التغييرات
          </button>
        </div>
      </div>
    </div>
  );
}
